/**
 * Action Button for Poker Reborn
 *
 * Specialized button for poker actions (fold, check, call, raise).
 *
 * @see docs/screens/room/ACTION_PANEL.md - Action button specs, states, behavior
 */

import { Container, Graphics, Text } from 'pixi.js';
import { log } from '@/utils/log';
import type { ActionType } from '@/types/game';

// =============================================================================
// STYLES
// =============================================================================

export const ACTION_BUTTON_STYLES: Record<ActionType | 'default', ActionButtonStyle> = {
  fold: {
    background: 0x9e9e9e,
    backgroundHover: 0x757575,
    backgroundPressed: 0x616161,
    text: 0xffffff,
    label: 'FOLD',
  },
  check: {
    background: 0x4caf50,
    backgroundHover: 0x43a047,
    backgroundPressed: 0x388e3c,
    text: 0xffffff,
    label: 'CHECK',
  },
  call: {
    background: 0x2196f3,
    backgroundHover: 0x1976d2,
    backgroundPressed: 0x1565c0,
    text: 0xffffff,
    label: 'CALL',
  },
  bet: {
    background: 0xff9800,
    backgroundHover: 0xf57c00,
    backgroundPressed: 0xef6c00,
    text: 0xffffff,
    label: 'BET',
  },
  raise: {
    background: 0xff9800,
    backgroundHover: 0xf57c00,
    backgroundPressed: 0xef6c00,
    text: 0xffffff,
    label: 'RAISE',
  },
  allin: {
    background: 0xf44336,
    backgroundHover: 0xe53935,
    backgroundPressed: 0xd32f2f,
    text: 0xffffff,
    label: 'ALL IN',
  },
  show: {
    background: 0x4caf50,
    backgroundHover: 0x43a047,
    backgroundPressed: 0x388e3c,
    text: 0xffffff,
    label: 'SHOW',
  },
  muck: {
    background: 0x607d8b,
    backgroundHover: 0x546e7a,
    backgroundPressed: 0x455a64,
    text: 0xffffff,
    label: 'MUCK',
  },
  default: {
    background: 0x3f51b5,
    backgroundHover: 0x3949ab,
    backgroundPressed: 0x303f9f,
    text: 0xffffff,
    label: '',
  },
};

interface ActionButtonStyle {
  background: number;
  backgroundHover: number;
  backgroundPressed: number;
  text: number;
  label: string;
}

// =============================================================================
// ACTION BUTTON
// =============================================================================

export class ActionButton extends Container {
  private actionType: ActionType;
  private buttonWidth: number;
  private buttonHeight: number;
  private style: ActionButtonStyle;

  private background: Graphics;
  private labelText: Text;
  private amountText: Text;

  private isPressed = false;
  private isHovered = false;
  private isEnabled = true;
  private amount: number | null = null;

  /** Callback when clicked */
  onClick: (() => void) | null = null;

  constructor(
    actionType: ActionType,
    options: {
      width?: number;
      height?: number;
      amount?: number;
    } = {}
  ) {
    super();

    this.actionType = actionType;
    this.buttonWidth = options.width ?? 100;
    this.buttonHeight = options.height ?? 60;
    this.amount = options.amount ?? null;
    this.style = ACTION_BUTTON_STYLES[actionType];

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create label text
    this.labelText = new Text({
      text: this.style.label,
      style: {
        fill: this.style.text,
        fontSize: 16,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    this.labelText.anchor.set(0.5);
    this.addChild(this.labelText);

    // Create amount text
    this.amountText = new Text({
      text: '',
      style: {
        fill: this.style.text,
        fontSize: 14,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.amountText.anchor.set(0.5);
    this.amountText.visible = false;
    this.addChild(this.amountText);

    // Initial draw
    this.draw();
    this.updateLayout();

    // Make interactive
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerdown', this.handlePointerDown, this);
    this.on('pointerup', this.handlePointerUp, this);
    this.on('pointerover', this.handlePointerOver, this);
    this.on('pointerout', this.handlePointerOut, this);
    this.on('pointerupoutside', this.handlePointerUpOutside, this);
  }

  /**
   * Set the amount to display (for call/raise buttons)
   */
  setAmount(amount: number | null): void {
    this.amount = amount;

    if (amount !== null && amount > 0) {
      this.amountText.text = this.formatAmount(amount);
      this.amountText.visible = true;
    } else {
      this.amountText.visible = false;
    }

    this.updateLayout();
  }

  /**
   * Set enabled state
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.alpha = enabled ? 1 : 0.5;
    this.cursor = enabled ? 'pointer' : 'default';
  }

  /**
   * Get the action type
   */
  getActionType(): ActionType {
    return this.actionType;
  }

  /**
   * Resize the button
   */
  resize(width: number, height: number): void {
    this.buttonWidth = width;
    this.buttonHeight = height;
    this.draw();
    this.updateLayout();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private draw(): void {
    this.background.clear();

    let color: number;
    if (!this.isEnabled) {
      color = this.style.background;
    } else if (this.isPressed) {
      color = this.style.backgroundPressed;
    } else if (this.isHovered) {
      color = this.style.backgroundHover;
    } else {
      color = this.style.background;
    }

    // Draw rounded rectangle
    this.background.roundRect(
      -this.buttonWidth / 2,
      -this.buttonHeight / 2,
      this.buttonWidth,
      this.buttonHeight,
      10
    );
    this.background.fill(color);

    // Add subtle border
    this.background.setStrokeStyle({ width: 2, color: 0x000000, alpha: 0.2 });
    this.background.stroke();
  }

  private updateLayout(): void {
    if (this.amountText.visible) {
      // Two lines: label on top, amount below
      this.labelText.y = -10;
      this.amountText.y = 10;
    } else {
      // Single line: centered
      this.labelText.y = 0;
    }
  }

  private formatAmount(amount: number): string {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount}`;
  }

  private handlePointerDown(): void {
    if (!this.isEnabled) return;
    this.isPressed = true;
    this.draw();
  }

  private handlePointerUp(): void {
    log.gamepad.debug`ActionButton.handlePointerUp: ${this.actionType}, enabled: ${this.isEnabled}, pressed: ${this.isPressed}`;
    if (!this.isEnabled) return;
    if (this.isPressed) {
      this.isPressed = false;
      this.draw();
      if (this.onClick) {
        log.gamepad.debug`ActionButton calling onClick for: ${this.actionType}`;
        this.onClick();
      } else {
        log.gamepad.warning`ActionButton: no onClick handler for ${this.actionType}`;
      }
    }
  }

  private handlePointerOver(): void {
    if (!this.isEnabled) return;
    this.isHovered = true;
    this.draw();
  }

  private handlePointerOut(): void {
    this.isHovered = false;
    this.draw();
  }

  private handlePointerUpOutside(): void {
    this.isPressed = false;
    this.draw();
  }
}
